import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { DemoBundle, FocusBoundary, Redaction, Step } from "../../../shared/types";
import { CALLOUT_REF_WIDTH, cardAt, hasArrow, isAttached, MAX_CALLOUT_PCT, snapTip, staysOnScreen, tipOf } from "../../../shared/callout";
import { pathScreens } from "../../../shared/tour";
import { clampFocusBoundary } from "../../../shared/focus";
import { api, imageUrl } from "../../api";
import { calloutLayerStyle, scaleCalloutLayer } from "../../calloutScale";
import { calloutAnchorAt, calloutElement, calloutTip } from "../../capture/anchorDom";
import CaptureFrame from "../../capture/CaptureFrame";
import { anchorShifts, clipUnanchored, EDITOR_STYLE_ATTR, openCapture, pickScroller, scrollPos, serializeCapture, SHOW_HIDDEN_CLASS, syncAnchors, type AnchorTrack } from "../../capture/hotspotAnchor";
import { IconBlur, IconClick, IconEyeOff, IconInfo, IconMessage, IconPlay, IconPlus, IconSettings, IconType, IconX } from "../../icons";
import { useEditor } from "../DemoEditor";
import { HotspotBoxes, RedoButton, round as roundPct, UndoButton, useHotspotCanvas } from "../hotspotCanvas";
import { RedactionBoxes, useRedactionCanvas } from "../redactionCanvas";
import Player from "../../player/Player";
import TextSlide from "../../player/TextSlide";
import { getSaveStatus, hasPendingSave, setSaveStatus, subscribePendingSave, subscribeSaveStatus, type SaveStatus } from "../dirty";
import CalloutCard, { useCallout } from "./CalloutCard";
import { DRAWER_ID } from "./compact";
import FocusBoundaryBox, { type FocusBoundaryHandle } from "./FocusBoundaryBox";
import { HotspotProps } from "./HotspotProps";
import { RedactionProps } from "./RedactionProps";

export type Mode = "hotspots" | "text" | "blur" | "hide";
const STAGE_BAR = 34; // height of the window bar above the page, px
const MODE_ICON: Record<Mode, React.ReactNode> = { hotspots: <IconClick />, text: <IconType />, blur: <IconBlur />, hide: <IconEyeOff /> };
// Multiples of the fit. Above 1 the canvas is bigger than its pane, which scrolls: for placing a box precisely.
const ZOOMS = [2, 1.5, 1, 0.75, 0.5];
type Op = { kind: "text"; at: number } | { kind: "mark"; el: Element; attr: string; had: boolean } | { kind: "hs" } | { kind: "redaction" };

// How far (page px) a dropped callout's arrow tip may be pulled onto the edge of the element it points at.
const SNAP_PX = 40;
// Kept in the saved HTML: blur/hide must survive into the replay.
const MARK_CSS = "[data-ih-blur]{filter:blur(7px)!important}[data-ih-hide]{visibility:hidden!important}";
// Editor-only, left out of what's saved (serializeCapture). Hidden parts stay fully hidden by
// default; the "Show hidden" toggle adds SHOW_HIDDEN_CLASS to <html> so they reappear as ghosts to restore.
const EDIT_CSS = "[contenteditable],html{outline:none}" +
  `.${SHOW_HIDDEN_CLASS} [data-ih-hide]{visibility:visible!important;opacity:.3!important;outline:2px dashed #C0262D!important;outline-offset:-2px}`;

// Short label for a hidden element in the restore list: tag, #id, first .class.
function describe(el: Element): string {
  const id = el.id ? `#${el.id}` : "";
  const cls = el.classList.length ? `.${el.classList[0]}` : "";
  return `${el.tagName.toLowerCase()}${id}${cls}`;
}

// Text, blur and hide only make sense on a captured page; screenshots and videos get hotspots only.
const ALL_MODES: [Mode, string, string][] = [
  ["hotspots", "Hotspots", "Drag on the page to draw a hotspot. Drag a box to move it, or its corner to resize. Click a hotspot to set what it does."],
  ["text", "Edit text", "Click any text and type to change it: names, numbers, labels."],
  ["blur", "Blur", "Click an element to blur it. Click it again to unblur."],
  ["hide", "Hide", "Click an element to hide it; its space stays. Hidden parts show as faded outlines here so you can click them again to bring them back."],
];

/** The screenshot, video, or text slide for a non-captured step, boxed the same way CaptureFrame boxes an iframe. */
function MediaFrame({ step, index, total, redactions }: { step: Step; index: number; total: number; redactions?: Redaction[] }) {
  const style: React.CSSProperties = { width: "100%", height: "100%", display: "block" };
  if (step.textCard) {
    return (
      <div className="capture-frame text-slide-frame" style={{ aspectRatio: `${step.imageWidth} / ${step.imageHeight}` }}>
        <TextSlide card={step.textCard} stepIndex={index} totalSteps={total} interactive={false} />
      </div>
    );
  }
  const reds = redactions ?? step.redactions ?? [];
  return (
    <div className="capture-frame" style={{ aspectRatio: `${step.imageWidth} / ${step.imageHeight}`, position: "relative" }}>
      {step.mediaType === "video" ? (
        <video src={imageUrl(step.imageKey)} muted preload="metadata" style={style} />
      ) : (
        <img src={imageUrl(step.imageKey)} alt="" draggable={false} style={style} />
      )}
      {reds.map((r) => (
        <div
          key={r.id}
          className={`redaction-overlay redaction-${r.type}`}
          style={{ left: `${r.x}%`, top: `${r.y}%`, width: `${r.w}%`, height: `${r.h}%` }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export interface CanvasHandle {
  /** Saves the screen if it has unsaved changes. Resolves false (error shown) when the save failed. */
  save: () => Promise<boolean>;
  isDirty: () => boolean;
}

interface Props {
  step: Step;
  index: number;
  total: number;
  /** The path the Studio is showing (null = the default path): Quick Preview plays that path. */
  pathId: string | null;
  mode: Mode;
  onModeChange: (m: Mode) => void;
  onSaved: (b: DemoBundle) => void;
  onDiscard: () => void;
  onDirtyChange: (dirty: boolean) => void;
  /** Screen-level settings, shown below whatever the current tool needs. */
  stepPanel: React.ReactNode;
  /** Pinned to the bottom of the inspector (delete, duplicate). */
  stepActions: React.ReactNode;
  /** A window under 1280px wide (see ./compact.ts): no window bar or hint under the canvas, the inspector is a drawer. */
  compact?: boolean;
  inspectorOpen?: boolean;
  onToggleInspector?: () => void;
}

/**
 * The studio's canvas and inspector for one screen: hotspots always, plus (for a captured page)
 * text, blur and hide, with one shared undo. Renders two grid children: the canvas pane with its
 * floating toolbar, and the inspector. A captured page renders in a same-origin frame with scripts
 * disabled, so the editor can change its DOM directly; a screenshot or video just sits under the
 * hotspot layer. Page edits save as a new copy of the HTML for this screen only.
 */
const StepCanvas = forwardRef<CanvasHandle, Props>(function StepCanvas(
  { step, index, total, pathId, mode: wantedMode, onModeChange, onSaved, onDiscard, onDirtyChange, stepPanel, stepActions, compact = false, inspectorOpen = false, onToggleInspector }, handle,
) {
  const frame = useRef<HTMLIFrameElement>(null);
  const area = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState<string | null>(step.captureKey ? null : "");
  const [hover, setHover] = useState<DOMRect | null>(null);
  const [ops, setOps] = useState<Op[]>([]);
  const [redoStack, setRedoStack] = useState<Op[]>([]);
  // Set while undo/redo replays a change, so the mutation observer and the hotspot-depth
  // effect below don't mistake the replay for a fresh edit and wipe the redo stack.
  const historyNav = useRef(false);
  const [fitRaw, setFitRaw] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenEls, setHiddenEls] = useState<Element[]>([]);
  const [saving, setSaving] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(hasPendingSave());
  useEffect(() => subscribePendingSave(setPending), []);
  const [saveStatus, setLocalSaveStatus] = useState<SaveStatus>(getSaveStatus());
  useEffect(() => subscribeSaveStatus((s) => setLocalSaveStatus(s)), []);

  const [scrollDirty, setScrollDirty] = useState(false);
  const [scrollShift, setScrollShift] = useState({ dx: 0, dy: 0 });
  const baselineScroll = useRef({ x: 0, y: 0 });
  const liveVisualFocus = useRef<FocusBoundary | null>(step.focus ?? null);
  useEffect(() => {
    liveVisualFocus.current = step.focus ?? null;
  }, [step.focus]);
  const { bundle: demo, go } = useEditor();
  const c = useHotspotCanvas(step.hotspots);
  const red = useRedactionCanvas(step.redactions ?? []);
  const co = useCallout(step);
  // A "Moves with Page" callout sits at a spot ON THE PAGE: its saved x/y are measured from the top-left of the
  // page's scrolling content (so it can be well below the first screen), and it is drawn that far back by however
  // far the page's main scroller is scrolled (`pickScroller`, the same one the player uses). So scrolling the
  // page never edits it, and saving it never touches the page's own saved scroll position.
  const [pageScroll, setPageScroll] = useState({ x: 0, y: 0 }); // px
  const scroller = useRef<HTMLElement | null>(null);
  const pageOff = { x: (pageScroll.x / step.imageWidth) * 100, y: (pageScroll.y / step.imageHeight) * 100 }; // % scrolled
  const calloutShift = { dx: -pageOff.x, dy: -pageOff.y };
  const focusBoxRef = useRef<FocusBoundaryHandle>(null);
  const [quickPreview, setQuickPreview] = useState(false);
  // Quick Preview is the Preview tab's player on the same saved bundle, so the two can't differ. It used to show the
  // saved page with the Studio's live hotspots, which are measured from wherever the Studio is scrolled now and may
  // have no anchor in the saved page yet: every box was off by however far the canvas had scrolled since the save.
  // Where it starts: this screen, on the Studio's path. null = it isn't on that path, so it is shown on its own.
  const quickStart = useMemo(() => {
    const index = pathScreens(demo, pathId).findIndex((s) => s.id === step.id);
    return index >= 0 ? { index } : null;
  }, [demo, pathId, step.id]);
  const fitWidth = Math.floor(fitRaw * zoom);
  // The callout's layers are laid out as a CALLOUT_REF_WIDTH-wide screen and scaled to the canvas (src/calloutScale.ts),
  // zoom included, exactly as the player scales them to its screen: so the card covers the same part of the page here,
  // at any zoom, as in Quick Preview, Preview and the published view, at any window size.
  // An attached callout (one with an arrow) hangs off its arrow's tip: where its element is now plus the tip's distance
  // from it (anchorDom's calloutTip, as the player does), polled with the hotspots. Page px, in the frame's viewport.
  const [calloutTipPx, setCalloutTipPx] = useState<[number, number] | null>(null);
  const calloutEl = useRef<{ key: string; el: Element | null; tries: number } | null>(null);
  const calloutTipAt = calloutTipPx && co.callout && isAttached(co.callout) && step.captureKey
    ? { x: (calloutTipPx[0] / step.imageWidth) * 100 + pageOff.x, y: (calloutTipPx[1] / step.imageHeight) * 100 + pageOff.y } // on the page, as x/y are
    : null;
  const stepLabel = `Step ${index + 1} of ${total}`;
  const ratio = step.imageWidth / step.imageHeight;

  const [savedOpsCount, setSavedOpsCount] = useState(0);
  const pageEdits = Math.abs(ops.length - savedOpsCount);
  const hasCanvasEdits = pageEdits > 0 || c.dirty || red.dirty;
  const localDirty = hasCanvasEdits || scrollDirty;
  const unsaved = localDirty || pending;

  // A failed save stays "error" (the toolbar's *Retry save*, the strip's *Save failed (retry)*) until a save succeeds:
  // the edit is still unsaved, and turning that back into "dirty" here hid the failure at once.
  useEffect(() => {
    if (unsaved && saveStatus !== "saving" && saveStatus !== "error") {
      setSaveStatus("dirty");
    } else if (!unsaved && !saving && saveStatus === "dirty") {
      setSaveStatus("saved");
    }
  }, [unsaved, saving, saveStatus]);

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!hasCanvasEdits || saving || (step.captureKey && html === null)) {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
      }
      return;
    }

    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      void latest.current.save();
    }, 1500);

    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
      }
    };
  }, [hasCanvasEdits, saving, step.captureKey, html]);
  const isScreenshot = !step.captureKey && !step.textCard && step.mediaType !== "video";
  const MODES: [Mode, string, string][] = step.captureKey
    ? ALL_MODES
    : isScreenshot
      ? [ALL_MODES[0], ["blur", "Redact", "Drag on the screenshot to draw a blur or blackout box. Click a box to change its style or delete it."]]
      : [ALL_MODES[0]];
  const mode: Mode = step.captureKey ? wantedMode : isScreenshot && wantedMode === "blur" ? "blur" : "hotspots";
  const setMode = onModeChange;

  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  const isPrepared = useRef(false);
  const lastLoadedCaptureKey = useRef<string | null>(null);

  useEffect(() => {
    if (!step.captureKey) {
      setHtml("");
      lastLoadedCaptureKey.current = null;
      return;
    }
    // If this captureKey was just saved by this canvas, the iframe already holds the content.
    if (step.captureKey === lastLoadedCaptureKey.current) return;
    lastLoadedCaptureKey.current = step.captureKey;
    api.captureSource(step.captureKey).then(setHtml).catch((e) => setError(e.message));
  }, [step.captureKey]);

  // Fit the page to the space available: as large as possible, never cropped. Measured inside the padding but
  // including any scrollbars, so the fit (and every zoom, a multiple of it) doesn't shrink when zooming in past it
  // makes the pane scroll.
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    const bar = compact ? 0 : STAGE_BAR; // the window bar above the page (not shown when compact)
    const ro = new ResizeObserver(([entry]) => {
      const cs = getComputedStyle(el);
      const box = entry.borderBoxSize?.[0];
      const width = (box ? box.inlineSize : el.offsetWidth) - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const height = (box ? box.blockSize : el.offsetHeight) - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      setFitRaw(Math.max(200, Math.floor(Math.min(width, (height - bar) * ratio))));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ratio, compact]);

  // Changing the zoom keeps whatever is in the middle of the pane in the middle (from Fit: the middle of the page).
  const zoomAround = useRef<{ fx: number; fy: number } | null>(null);
  const changeZoom = (z: number) => {
    if (z === zoom) return;
    const pane = area.current, stage = pane?.querySelector(".editor-stage");
    if (pane && stage) {
      const p = pane.getBoundingClientRect(), r = stage.getBoundingClientRect();
      zoomAround.current = { fx: (p.left + pane.clientWidth / 2 - r.left) / r.width, fy: (p.top + pane.clientHeight / 2 - r.top) / r.height };
    }
    setZoom(z);
  };
  useLayoutEffect(() => {
    const at = zoomAround.current, pane = area.current, stage = pane?.querySelector(".editor-stage");
    if (!at || !pane || !stage) return;
    zoomAround.current = null;
    const p = pane.getBoundingClientRect(), r = stage.getBoundingClientRect();
    pane.scrollLeft += r.left + at.fx * r.width - (p.left + pane.clientWidth / 2);
    pane.scrollTop += r.top + at.fy * r.height - (p.top + pane.clientHeight / 2);
  }, [fitWidth]);

  // Each new hotspot change is one step in the shared undo history.
  const hsDepth = useRef(0);
  useEffect(() => {
    if (c.depth > hsDepth.current) {
      if (!historyNav.current) setRedoStack([]);
      setOps((o) => [...o, { kind: "hs" }]);
    }
    hsDepth.current = c.depth;
  }, [c.depth]);

  const redDepth = useRef(0);
  useEffect(() => {
    if (red.depth > redDepth.current) {
      if (!historyNav.current) setRedoStack([]);
      setOps((o) => [...o, { kind: "redaction" }]);
    }
    redDepth.current = red.depth;
  }, [red.depth]);

  const doc = () => frame.current?.contentDocument ?? null;

  // Hotspots follow the page as it scrolls. `track` remembers where each one's anchor element
  // was on screen; `geom` remembers the box each one should have if only scrolling moved it, so a
  // box that differs (drawn, dragged, resized, undone) is known to be an edit and gets re-anchored.
  const track = useRef(new Map<string, AnchorTrack>());
  const geom = useRef(new Map<string, { x: number; y: number; w: number; h: number }>());
  const live = useRef({ hs: c.hs, shift: c.shift, callout: co.callout });
  live.current = { hs: c.hs, shift: c.shift, callout: co.callout };

  const syncEdited = () => {
    const d = doc();
    if (!d || !step.captureKey) return;
    const near = (a: number, b: number) => Math.abs(a - b) < 0.01;
    const edited = c.hs.filter((h) => {
      const g = geom.current.get(h.id);
      return !g || !near(g.x, h.x) || !near(g.y, h.y) || !near(g.w, h.w) || !near(g.h, h.h);
    });
    if (!edited.length) return;
    syncAnchors(d, edited, step.imageWidth, step.imageHeight);
    for (const h of edited) {
      geom.current.set(h.id, { x: h.x, y: h.y, w: h.w, h: h.h });
      track.current.delete(h.id); // re-read the anchor's position as the new baseline
    }
  };

  // Poll the anchors every frame rather than listen for scroll events: it also catches a
  // scrollbar drag, an inner scroller, Edit-text scrolling and layout shifts, and needs no
  // handlers attached inside the sandboxed page.
  useEffect(() => {
    if (!step.captureKey) return;
    let raf = 0;
    let frame = 0;
    const tick = () => {
      const d = doc();
      const { hs, shift } = live.current;
      if (d && d.readyState === "complete") {
        if (hs.length) {
          const by = anchorShifts(d, hs, track.current, step.imageWidth, step.imageHeight);
          if (by.size) {
            by.forEach((s, id) => {
              const g = geom.current.get(id);
              // Rounded exactly as `shift` rounds the hotspot itself: a raw sum would drift a
              // fraction per frame, and once that drift passes `syncEdited`'s tolerance every
              // box reads as hand-edited and gets re-anchored in the middle of a scroll.
              if (g) { g.x = roundPct(g.x + s.dx); g.y = roundPct(g.y + s.dy); }
            });
            shift(by);
          }
        }
        if (isPrepared.current) {
          if (!scroller.current && frame++ % 45 === 0) scroller.current = pickScroller(d); // nothing scrolled yet: look again
          const [px, py] = scrollPos(d, scroller.current);
          setPageScroll((prev) => (Math.abs(prev.x - px) > 0.05 || Math.abs(prev.y - py) > 0.05 ? { x: px, y: py } : prev));
          const diffX = px - baselineScroll.current.x;
          const diffY = py - baselineScroll.current.y;
          const dirty = Math.abs(diffX) > 1 || Math.abs(diffY) > 1;
          setScrollDirty((prev) => (prev !== dirty ? dirty : prev));
          const dxPct = -(diffX / step.imageWidth) * 100;
          const dyPct = -(diffY / step.imageHeight) * 100;
          setScrollShift((prev) => (Math.abs(prev.dx - dxPct) > 0.05 || Math.abs(prev.dy - dyPct) > 0.05 ? { dx: dxPct, dy: dyPct } : prev));
          // An attached callout: its element, found once (and again if its anchor changes, or until the page lays it
          // out), then where its arrow points now.
          const cur = live.current.callout;
          const a = cur && isAttached(cur) ? cur.anchor! : null;
          let tipNow: [number, number] | null = null;
          if (a) {
            const key = JSON.stringify(a);
            if (calloutEl.current?.key !== key) calloutEl.current = { key, el: null, tries: 0 };
            const ce = calloutEl.current;
            if (!ce.el && ce.tries++ < 300) ce.el = calloutElement(d, scroller.current, a);
            tipNow = ce.el && calloutTip(d, ce.el, a);
          }
          setCalloutTipPx((prev) => (!tipNow ? null : prev && Math.abs(prev[0] - tipNow[0]) < 0.05 && Math.abs(prev[1] - tipNow[1]) < 0.05 ? prev : tipNow));
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [step.captureKey, step.imageWidth, step.imageHeight]);

  const refreshHidden = () => {
    const d = doc();
    setHiddenEls(d ? Array.from(d.querySelectorAll("[data-ih-hide]")) : []);
  };

  const prepare = () => {
    const d = doc();
    if (!d || !d.head || !d.body) return;
    // A page can have no layout yet when its frame loads (shown late, by an animation say): it can't be scrolled to
    // its saved position or measured, so every box would be placed at the wrong scroll and an unanchored one would
    // find nothing to follow. Wait until it has a layout, as the player's replay script does (defect 8 in
    // docs/annotation-anchoring.md).
    if (!d.documentElement.getClientRects().length || !d.body.getClientRects().length) {
      requestAnimationFrame(() => { if (doc() === d) prepare(); });
      return;
    }
    if (!d.querySelector("style[data-ih-marks]")) {
      const s = d.createElement("style");
      s.setAttribute("data-ih-marks", "");
      s.textContent = MARK_CSS;
      d.head.appendChild(s);
    }
    const e = d.createElement("style");
    e.setAttribute(EDITOR_STYLE_ATTR, "");
    e.textContent = EDIT_CSS;
    d.head.appendChild(e);
    d.designMode = mode === "text" ? "on" : "off";
    refreshHidden();
    // Every hotspot is measured at the scroll position the page was saved with, and only then does the page
    // go to the top: the order the player opens it in, so each box starts where Preview shows it.
    const open = openCapture(d, live.current.hs, step.imageWidth, step.imageHeight);
    scroller.current = open.scroller;
    track.current = open.track;
    for (const h of live.current.hs) geom.current.set(h.id, { x: h.x, y: h.y, w: h.w, h: h.h });
    const [topX, topY] = scrollPos(d, scroller.current);
    baselineScroll.current = { x: topX, y: topY };
    setPageScroll({ x: topX, y: topY });
    setScrollShift({ dx: 0, dy: 0 });
    setScrollDirty(false);
    isPrepared.current = true;
    // An older "Moves with Page" callout is measured from where the page was saved scrolled to, not from the top
    // (the player's initPageX / initPageY).
    co.adoptPageCoords({ x: (open.opened[0] / step.imageWidth) * 100, y: (open.opened[1] / step.imageHeight) * 100 });
    for (const ms of [1200, 3000]) setTimeout(() => { const cur = doc(); if (cur === d) scroller.current = pickScroller(cur); }, ms);

    // Typing bursts become undo steps. Only tracked when user is actively in text editing mode.
    new MutationObserver(() => {
      if (!isPrepared.current || modeRef.current !== "text") return;
      if (!historyNav.current) setRedoStack([]);
      setOps((o) => {
        const last = o[o.length - 1];
        if (last?.kind === "text" && Date.now() - last.at < 1200) return [...o.slice(0, -1), { kind: "text", at: Date.now() }];
        return [...o, { kind: "text", at: Date.now() }];
      });
    }).observe(d.body, { subtree: true, characterData: true, childList: true });
  };

  useEffect(() => {
    const d = doc();
    if (d?.readyState === "complete") d.designMode = mode === "text" ? "on" : "off";
    setHover(null);
  }, [mode]);

  // Keep each hotspot's anchor element in sync with where its box currently is, so a hotspot
  // placed on a scrolled capture stays attached to that element rather than a bare coordinate.
  useEffect(() => {
    syncEdited();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.hs]);

  // The "Show hidden" toggle only applies while in Hide mode.
  useEffect(() => {
    const d = doc();
    if (!d) return;
    d.documentElement.classList.toggle(SHOW_HIDDEN_CLASS, mode === "hide" && showHidden);
  }, [mode, showHidden, html]);

  const undo = () => {
    const last = ops[ops.length - 1];
    if (!last) return;
    historyNav.current = true;
    if (last.kind === "hs") { hsDepth.current = Math.max(0, c.depth - 1); c.undo(); }
    else if (last.kind === "redaction") { redDepth.current = Math.max(0, red.depth - 1); red.undo(); }
    else if (last.kind === "mark") {
      if (last.had) last.el.setAttribute(last.attr, ""); else last.el.removeAttribute(last.attr);
      if (last.attr === "data-ih-hide") refreshHidden();
    }
    else {
      const d = doc();
      if (d) { d.designMode = "on"; d.execCommand("undo"); d.designMode = mode === "text" ? "on" : "off"; }
    }
    setRedoStack((r) => [...r, last]);
    // Text undo itself mutates the page; drop the entry that echo creates as well as this one.
    setTimeout(() => { setOps((o) => o.slice(0, Math.max(0, ops.length - 1))); historyNav.current = false; }, 0);
  };

  const redo = () => {
    const last = redoStack[redoStack.length - 1];
    if (!last) return;
    historyNav.current = true;
    if (last.kind === "hs") { c.redo(); }
    else if (last.kind === "redaction") { red.redo(); }
    else if (last.kind === "mark") {
      if (last.had) last.el.removeAttribute(last.attr); else last.el.setAttribute(last.attr, "");
      setOps((o) => [...o, last]);
    } else {
      const d = doc();
      if (d) { d.designMode = "on"; d.execCommand("redo"); d.designMode = mode === "text" ? "on" : "off"; }
    }
    setRedoStack((r) => r.slice(0, -1));
    setTimeout(() => { historyNav.current = false; }, 0);
  };

  // The scrollbar track/thumb that (x,y), in the frame's own unscaled pixels, lands on (as a
  // rect in those pixels), or null when it's on content — walking up from the element at that
  // point to the document itself (whose scrollbar the browser draws even though its computed
  // overflow is the default "visible"). While hovering one, the overlay leaves a hole over that
  // strip (passthroughHole) so a mousedown there starts a real scrollbar drag on the page instead
  // of drawing a hotspot or picking an element to blur/hide — CaptureFrame keeps the iframe itself
  // interactive so that drag has somewhere to land. (Listening for the drag directly on the
  // iframe's own sandboxed document, tried first, made Chromium hang mid-drag — this is
  // deliberately overlay-side only.)
  const scrollbarStrip = (d: Document, el: Element, x: number, y: number): DOMRect | null => {
    let node: Element | null = el;
    while (node) {
      const cs = getComputedStyle(node);
      const root = node === d.documentElement;
      const canY = root ? cs.overflowY !== "hidden" && node.scrollHeight > node.clientHeight
        : (cs.overflowY === "auto" || cs.overflowY === "scroll") && node.scrollHeight > node.clientHeight;
      const canX = root ? cs.overflowX !== "hidden" && node.scrollWidth > node.clientWidth
        : (cs.overflowX === "auto" || cs.overflowX === "scroll") && node.scrollWidth > node.clientWidth;
      if (canY || canX) {
        const r = node.getBoundingClientRect();
        const sbW = r.width - node.clientWidth;
        const sbH = r.height - node.clientHeight;
        if (canY && sbW > 0 && x >= r.right - sbW) return new DOMRect(r.right - sbW, r.top, sbW, r.height);
        if (canX && sbH > 0 && y >= r.bottom - sbH) return new DOMRect(r.left, r.bottom - sbH, r.width, sbH);
      }
      node = node.parentElement;
    }
    return null;
  };
  // Run on every pointer move over the hotspots/blur/hide overlay: cut a hole in the overlay
  // (clip-path, which hit-testing honours) exactly over a scrollbar right before a drag on it would
  // start, and close it once the pointer is back on the overlay anywhere else. A hole, not
  // `pointer-events: none` on the whole overlay as before: that made the pointer leave the overlay
  // on its next move, the leave put the overlay back, the move after took it away again, so
  // whether a mousedown reached the scrollbar depended on how many moves had landed on it (on a
  // canvas big enough for a scrollbar to be more than a few screen px wide, about half the time).
  const passthroughHole = useRef<DOMRect | null>(null); // where the hole is, in the admin page's px
  const updatePassthrough = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = doc();
    const el = e.currentTarget;
    if (!d) return;
    const r = el.getBoundingClientRect();
    const scale = r.width / step.imageWidth;
    const x = (e.clientX - r.left) / scale;
    const y = (e.clientY - r.top) / scale;
    const target = d.elementFromPoint(x, y);
    const strip = target && scrollbarStrip(d, target, x, y);
    if (!strip) {
      if (passthroughHole.current) { el.style.clipPath = ""; passthroughHole.current = null; }
      return;
    }
    const [x0, y0, x1, y1] = [strip.left * scale, strip.top * scale, strip.right * scale, strip.bottom * scale];
    el.style.clipPath = `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${x0}px ${y0}px, ${x0}px ${y1}px, ${x1}px ${y1}px, ${x1}px ${y0}px, ${x0}px ${y0}px)`;
    passthroughHole.current = new DOMRect(r.left + x0, r.top + y0, x1 - x0, y1 - y0);
  };
  // Leaving the overlay into its own hole is the point of the hole; leaving it any other way closes it.
  const leavePassthrough = (e: React.PointerEvent<HTMLDivElement>) => {
    const h = passthroughHole.current;
    if (h && e.clientX >= h.left - 1 && e.clientX <= h.right + 1 && e.clientY >= h.top - 1 && e.clientY <= h.bottom + 1) return;
    e.currentTarget.style.clipPath = "";
    passthroughHole.current = null;
  };

  // The overlay (hotspots/blur/hide) sits on top of the frame and takes the pointer, so the
  // wheel never reaches the page. Forward it to whatever scrolls under the pointer — an inner
  // scrollable element, or the page itself. Safe in every mode: hotspots track an anchor element
  // rather than a bare coordinate, and the final scroll position is saved with them (see save()).
  // A numerically taller-than-its-box element isn't necessarily a scroll container: with the
  // page's own overflow:visible (the default), content just spills out unclipped and scrollTop
  // has no visible effect. Only auto/scroll actually clip-and-scroll.
  const overflows = (el: Element, axis: "y" | "x") => {
    const overflow = axis === "y" ? getComputedStyle(el).overflowY : getComputedStyle(el).overflowX;
    return overflow === "auto" || overflow === "scroll" || overflow === "overlay";
  };
  const overflowing = (el: Element, axis: "y" | "x") => (axis === "y" ? el.scrollHeight > el.clientHeight : el.scrollWidth > el.clientWidth);
  /** Whether `el` can still scroll in the direction of a wheel turn (by more than a pixel: rounding isn't room). */
  const pans = (el: Element, dx: number, dy: number) =>
    (dy > 0 && el.scrollTop < el.scrollHeight - el.clientHeight - 1) || (dy < 0 && el.scrollTop > 1) ||
    (dx > 0 && el.scrollLeft < el.scrollWidth - el.clientWidth - 1) || (dx < 0 && el.scrollLeft > 1);
  const onWheel = (e: WheelEvent, layer: Element) => {
    if (e.ctrlKey) return; // the browser's own zoom (Ctrl+wheel, a trackpad pinch): not a scroll
    // Zoomed in past Fit, the canvas is bigger than its pane: the wheel first moves around it (the browser scrolls the
    // pane itself, once this returns) and scrolls the page only once the pane is at that edge. At Fit or below the
    // pane never scrolls, so this never applies.
    const pane = area.current;
    if (pane && pans(pane, e.deltaX, e.deltaY)) return;
    e.preventDefault();
    const d = doc();
    if (!d) return;
    const r = layer.getBoundingClientRect();
    const scale = r.width / step.imageWidth;
    const x = (e.clientX - r.left) / scale;
    const y = (e.clientY - r.top) / scale;
    // Candidates, best first: properly-scrollable ancestors of the element under the pointer,
    // then the nearest one that's merely taller than its box (better than nothing if the page
    // does something unusual with overflow), then <body> and <html>. Many real pages scroll on
    // <body> itself (html{overflow:hidden}, body{overflow:auto}), so it must be tried too, not
    // just the scrolling element.
    const strict: Element[] = [];
    let loose: Element | null = null;
    for (let el = d.elementFromPoint(x, y); el && el !== d.documentElement && el !== d.body; el = el.parentElement) {
      if (overflows(el, "y") || overflows(el, "x")) { if (overflowing(el, "y") || overflowing(el, "x")) strict.push(el); }
      else if (!loose && (overflowing(el, "y") || overflowing(el, "x"))) loose = el;
    }
    const candidates = [...strict, ...(loose ? [loose] : []), d.body, d.scrollingElement, d.documentElement]
      .filter((el, i, all): el is HTMLElement => !!el && all.indexOf(el) === i);
    // Scroll the first candidate that actually moves, so a scroller already at its end hands the
    // wheel on to the next one out, as a real browser does.
    for (const scrollEl of candidates) {
      // A page's own scroll-behavior:smooth (often set globally) would fight repeated wheel ticks;
      // a scroll wheel or line-mode mouse can report tiny per-tick deltas that read as "stuck".
      scrollEl.style.scrollBehavior = "auto";
      const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? scrollEl.clientHeight || 800 : 1;
      const { scrollTop, scrollLeft } = scrollEl;
      scrollEl.scrollBy(e.deltaX * factor, e.deltaY * factor);
      if (scrollEl.scrollTop !== scrollTop || scrollEl.scrollLeft !== scrollLeft) break;
    }
  };
  // A native listener, not React's onWheel: React's is passive, so by the time it ran the browser could already have
  // scrolled the pane (a zoomed canvas) on its own, and the wheel went both to the pane and to the page.
  const wheelRef = useRef(onWheel);
  wheelRef.current = onWheel;
  useEffect(() => {
    const pane = area.current;
    if (!pane) return;
    const h = (e: WheelEvent) => {
      const layer = (e.target as Element).closest?.(".hs-layer, .pick-layer");
      if (layer) wheelRef.current(e, layer);
    };
    pane.addEventListener("wheel", h, { passive: false });
    return () => pane.removeEventListener("wheel", h);
  }, []);

  // Blur/Hide: an overlay takes the pointer and maps it into the scaled page.
  const target = (e: React.PointerEvent<HTMLDivElement>): Element | null => {
    const d = doc();
    if (!d) return null;
    const r = e.currentTarget.getBoundingClientRect();
    const scale = r.width / step.imageWidth;
    const el = d.elementFromPoint((e.clientX - r.left) / scale, (e.clientY - r.top) / scale);
    return el && el !== d.documentElement && el !== d.body ? el : null;
  };
  const onPickMove = (e: React.PointerEvent<HTMLDivElement>) => {
    updatePassthrough(e);
    const el = target(e);
    if (!el) return setHover(null);
    const b = el.getBoundingClientRect();
    const scale = e.currentTarget.getBoundingClientRect().width / step.imageWidth;
    setHover(new DOMRect(b.x * scale, b.y * scale, b.width * scale, b.height * scale));
  };
  const onPick = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = target(e);
    if (!el) return;
    const attr = mode === "blur" ? "data-ih-blur" : "data-ih-hide";
    const had = el.hasAttribute(attr);
    if (had) el.removeAttribute(attr); else el.setAttribute(attr, "");
    setOps((o) => [...o, { kind: "mark", el, attr, had }]);
    setRedoStack([]);
    setHover(null);
    if (attr === "data-ih-hide") refreshHidden();
  };

  const restore = (el: Element) => {
    const had = el.hasAttribute("data-ih-hide");
    el.removeAttribute("data-ih-hide");
    setOps((o) => [...o, { kind: "mark", el, attr: "data-ih-hide", had }]);
    refreshHidden();
  };

  // The save running now, if any (an autosave, usually).
  const inflight = useRef<Promise<boolean> | null>(null);
  const save = async (manual = false): Promise<boolean> => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
    // A save asked for while another is running (Ctrl+S, leaving the screen or Quick Preview during an autosave)
    // waits for it, then saves whatever is still unsaved. It used to report success at once: the Save button stayed
    // on "Saving…" for good, and the Studio left the screen (or opened Quick Preview) before the save had happened.
    if (inflight.current) {
      if (!manual) return true;
      setManualSaving(true);
      try {
        if (!(await inflight.current)) return false;
      } finally {
        setManualSaving(false);
      }
      await new Promise((r) => setTimeout(r, 0)); // let the canvas catch up with what that save stored
      return latest.current.save(true);
    }
    if (!unsaved && saveStatus !== "error") return true;
    const run = saveNow(manual);
    inflight.current = run;
    try {
      return await run;
    } finally {
      if (inflight.current === run) inflight.current = null;
    }
  };
  const saveNow = async (manual: boolean): Promise<boolean> => {
    setSaving(true);
    if (manual) {
      setManualSaving(true);
      setSaveStatus("saving");
    }
    // The error stays up until a save succeeds: clearing it for each autosave retry made the banner, and the whole
    // canvas under it, jump up and down every second and a half while a save kept failing.
    try {
      await co.flush();
      await focusBoxRef.current?.flush();
      const d = doc();
      // The boxes already follow the page, so they match what's on screen — and the scroll
      // position saved below. An anchored one that the page is currently scrolled past therefore
      // sits off the frame, which is exactly right: the player puts it back at "saved box + how
      // far its element has moved since" and it comes into view as the viewer scrolls to it.
      // Only a hotspot the page has no anchor for has to be clamped, because nothing will ever
      // move it back in; clamping an anchored one would flatten it to a sliver at the edge of the
      // viewport for good.
      const hsToSave = step.captureKey ? clipUnanchored(d, c.hs) : c.hs;
      let bundle: DemoBundle | null = null;
      const isScrolled = Math.abs(pageScroll.x) > 1 || Math.abs(pageScroll.y) > 1;
      if (step.captureKey && d && (pageEdits > 0 || c.dirty || scrollDirty || (co.callout && !staysOnScreen(co.callout) && isScrolled))) {
        // A hotspot's box and focal boundary only mean something against the page it was drawn on — its scroll
        // position and the anchor tags written into it — so page, hotspots, and focal boundary go in one request.
        const focusToSave = step.focus ? clampFocusBoundary({
          ...step.focus,
          ...(liveVisualFocus.current ? {
            x: liveVisualFocus.current.x,
            y: liveVisualFocus.current.y,
            w: liveVisualFocus.current.w,
            h: liveVisualFocus.current.h,
          } : {
            x: step.focus.x + scrollShift.dx,
            y: step.focus.y + scrollShift.dy,
          }),
        }) : undefined;
        bundle = await api.saveStepCapture(step.id, serializeCapture(d, hsToSave), hsToSave, focusToSave);
      } else {
        if (red.dirty) {
          bundle = await api.updateStep(step.id, { redactions: red.redactions });
        }
        if (c.dirty) {
          bundle = await api.saveHotspots(step.id, hsToSave);
        }
      }
      if (bundle) {
        c.markClean(bundle.steps.find((x) => x.id === step.id)?.hotspots ?? []);
        red.markClean(bundle.steps.find((x) => x.id === step.id)?.redactions ?? []);
        setSavedOpsCount(ops.length);
        setScrollDirty(false);
        setScrollShift({ dx: 0, dy: 0 });
        if (d) {
          const [sx, sy] = scrollPos(d, scroller.current);
          baselineScroll.current = { x: sx, y: sy };
        }
        const updatedStep = bundle.steps.find((x) => x.id === step.id);
        if (updatedStep?.captureKey) {
          lastLoadedCaptureKey.current = updatedStep.captureKey;
        }
        liveVisualFocus.current = updatedStep?.focus ?? null;
        onSaved(bundle);
      }
      setError(null);
      setSaveStatus("saved");
      return true;
    } catch (err: any) {
      setError(err.message);
      setSaveStatus("error", err.message);
      return false;
    } finally {
      setSaving(false);
      if (manual) setManualSaving(false);
    }
  };
  const discard = () => { if (confirm("Discard your changes to this screen?")) onDiscard(); };
  /** Save what's unsaved first, so Quick Preview shows this screen exactly as Preview (and a viewer) will. */
  const openQuickPreview = async () => {
    if ((hasCanvasEdits || pending) && !(await save(true))) return; // the error is shown; nothing to preview yet
    setQuickPreview(true);
  };

  // The studio saves before switching screens, and asks whether there's anything to save.
  const latest = useRef({ save, unsaved });
  latest.current = { save, unsaved };
  useImperativeHandle(handle, () => ({ save: () => latest.current.save(true), isDirty: () => latest.current.unsaved }), []);
  useEffect(() => { onDirtyChange(unsaved); }, [unsaved, onDirtyChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable;
      const plain = !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
      if (e.key === "Escape") {
        if (quickPreview) {
          e.preventDefault();
          e.stopPropagation();
          setQuickPreview(false);
          return;
        }
        if (!typing) {
          if (c.sel) c.setSel(null);
          if (red.sel) red.setSel(null);
        }
      }
      else if (plain && !typing && e.key.toLowerCase() === "p" && !quickPreview) { e.preventDefault(); void openQuickPreview(); }
      else if (plain && !typing && e.key.toLowerCase() === "h") { e.preventDefault(); addHotspot(); }
      else if (plain && !typing && e.key.toLowerCase() === "r" && isScreenshot) { e.preventDefault(); setMode("blur"); red.addCentered("blur"); }
      else if (plain && !typing && e.key.toLowerCase() === "c" && !co.callout) { e.preventDefault(); addCallout(); }
      else if (plain && !typing && e.key.toLowerCase() === "i" && compact && !quickPreview) { e.preventDefault(); onToggleInspector?.(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !typing) { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); void save(true); }
      else if ((e.key === "Delete" || e.key === "Backspace") && !typing) {
        if (mode === "hotspots" && c.sel) { e.preventDefault(); c.removeSel(); }
        else if (mode === "blur" && isScreenshot && red.sel) { e.preventDefault(); red.removeSel(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const hint = MODES.find(([k]) => k === mode)![2] +
    (mode === "hotspots" && step.captureKey ? " Scroll to reach more of the page — a hotspot stays attached to what it's drawn on." : "") +
    (zoom > 1 && mode !== "text" ? " Zoomed in: the wheel moves around the canvas first, then scrolls the page." : "");

  const addHotspot = () => { setMode("hotspots"); c.addCentered(); };

  // A new callout appears in the part of the page on screen now. (Its saved x/y are page coordinates, so
  // that is the screen position plus however far the page is scrolled.)
  const addCallout = () => co.add(pageOff);

  // Switching mode keeps the callout where it is on screen: screen coordinates and page coordinates differ by
  // how far the page is scrolled. Going onto the screen, one scrolled out of view lands at the nearest edge.
  const setCalloutMode = (mode: "pinned" | "sticky") => {
    const cur = co.callout;
    if (!cur || (mode === "sticky") === staysOnScreen(cur)) return;
    if (mode === "sticky") co.patch({ positionMode: "sticky", x: Math.min(100, Math.max(0, cur.x - pageOff.x)), y: Math.min(100, Math.max(0, cur.y - pageOff.y)) });
    else co.patch({ positionMode: "pinned", pageCoords: true, x: Math.min(MAX_CALLOUT_PCT, cur.x + pageOff.x), y: Math.min(MAX_CALLOUT_PCT, cur.y + pageOff.y) });
  };

  // Attach a callout with an arrow to what its arrow points at, where its card is now: the element under the tip (as
  // the player will find it), the tip moved to that element's edge facing the card (snapTip), and the card hung off it.
  // Nothing there to attach to (bare background, a screenshot): it stays where it is, at its spot on the page.
  const latestCallout = useRef(co.callout);
  latestCallout.current = co.callout;
  const attachCallout = (snap: boolean) => {
    const cur = latestCallout.current;
    const d = doc();
    const stageEl = area.current?.querySelector(".editor-stage");
    const cardEl = stageEl?.querySelector(".callout-edit");
    if (!cur || !hasArrow(cur) || !step.captureKey || !d || !isPrepared.current || !stageEl || !cardEl) return;
    const s = stageEl.getBoundingClientRect(), r = cardEl.getBoundingClientRect();
    const f = step.imageWidth / s.width; // page px per screen px
    const card = { x: (r.left - s.left) * f, y: (r.top - s.top) * f, w: r.width * f, h: r.height * f };
    const k = step.imageWidth / CALLOUT_REF_WIDTH;
    const arrow = cur.arrow!;
    const tip = tipOf(arrow, card, k);
    const screen = { w: step.imageWidth, h: step.imageHeight };
    // Let go after a drag, the tip is pulled onto the edge of what it points at when that is close (a nudge, never a
    // jump); an arrow picked in the inspector attaches the card exactly where it is.
    const got = calloutAnchorAt(d, scroller.current, tip, tip, (t, el) => {
      if (!snap) return t;
      const to = snapTip(arrow, t, { x: el.left, y: el.top, w: el.right - el.left, h: el.bottom - el.top }, screen);
      return Math.hypot(to[0] - t[0], to[1] - t[1]) <= SNAP_PX ? to : t;
    });
    if (!got) return;
    const at = calloutTip(d, got.el, got.anchor)!;
    const [cx, cy] = cardAt(arrow, at, card.w, card.h, k);
    calloutEl.current = { key: JSON.stringify(got.anchor), el: got.el, tries: 0 };
    setCalloutTipPx(at);
    co.patch({
      anchor: got.anchor, positionMode: "pinned", pageCoords: true,
      x: Math.round(Math.min(MAX_CALLOUT_PCT, Math.max(0, (cx / step.imageWidth) * 100 + pageOff.x)) * 10) / 10,
      y: Math.round(Math.min(MAX_CALLOUT_PCT, Math.max(0, (cy / step.imageHeight) * 100 + pageOff.y)) * 10) / 10,
    });
  };

  // A new arrow keeps the card where it is and attaches it to what the new arrow points at; no arrow detaches it, and
  // it stays where it is, at that spot on the page.
  const setCalloutArrow = (dir: "none" | "top" | "bottom" | "left" | "right") => {
    const cur = co.callout;
    if (!cur) return;
    const here = cur.anchor ? cardOnPage() : null;
    co.patch({ arrow: dir, ...(cur.anchor ? { anchor: null, ...(here ?? {}) } : {}) });
    if (dir !== "none" && !cur.modal) requestAnimationFrame(() => attachCallout(false));
  };
  // The card's top-left on the page right now (% of the screen, as a Moves-with-Page callout's x/y).
  const cardOnPage = () => {
    const stageEl = area.current?.querySelector(".editor-stage");
    const cardEl = stageEl?.querySelector(".callout-edit");
    if (!stageEl || !cardEl) return null;
    const s = stageEl.getBoundingClientRect(), r = cardEl.getBoundingClientRect();
    return {
      x: Math.round(Math.max(0, ((r.left - s.left) / s.width) * 100 + pageOff.x) * 10) / 10,
      y: Math.round(Math.max(0, ((r.top - s.top) / s.height) * 100 + pageOff.y) * 10) / 10,
    };
  };

  // A callout further down the page than the screen shows can't be seen: scroll the page to it.
  const scrollToCallout = () => {
    const el = scroller.current;
    const cur = co.callout;
    if (!el || !cur) return;
    el.scrollTo({ top: Math.max(0, (cur.y / 100) * step.imageHeight - 80), left: Math.max(0, (cur.x / 100) * step.imageWidth - 40), behavior: "smooth" });
  };

  const zoomGroup = (
    <div className={`tool-group ${compact ? "" : "zoom-float"}`} role="radiogroup" aria-label="Zoom">
      {ZOOMS.map((z) => (
        <button key={z} type="button" role="radio" aria-checked={zoom === z} className={`tool tool-zoom ${zoom === z ? "on" : ""}`} onClick={() => changeZoom(z)}
          title={z === 1 ? "Fit to the window" : z > 1 ? `${z * 100}% of fit: the canvas scrolls` : `${z * 100}% of fit`}>{z === 1 ? "Fit" : `${z * 100}%`}</button>
      ))}
    </div>
  );

  const actionLabel = (h: { action: string; tooltipText: string }) =>
    h.action === "show_tooltip" ? (h.tooltipText ? `Tooltip: ${h.tooltipText.slice(0, 32)}` : "Tooltip")
      : h.action === "goto_step" ? "Jump to a screen" : h.action === "open_link" ? "Open a link" : "Go to next screen";

  return (
    <>
      <section className="studio-canvas" aria-label={`Screen ${index + 1} of ${total}`}>
        <div className="editor-bar studio-toolbar" role="toolbar" aria-label="Editing tools">
          <div className="tool-group" role="radiogroup" aria-label="Editing mode">
            {MODES.map(([k, label]) => (
              <button key={k} type="button" role="radio" aria-checked={mode === k} aria-label={label} title={label} className={`tool ${mode === k ? "on" : ""}`} onClick={() => setMode(k)}>{MODE_ICON[k]}<span className="tb-label">{label}</span></button>
            ))}
          </div>
          <span className="tool-sep" aria-hidden />
          <UndoButton compact onClick={undo} disabled={ops.length === 0} />
          <RedoButton compact onClick={redo} disabled={redoStack.length === 0} />
          <span className="tool-sep" aria-hidden />
          <button type="button" className="btn btn-sm btn-ghost" onClick={addHotspot} aria-label="Add hotspot" title="Add a hotspot (H)"><IconPlus /><span className="tb-label opt">Add hotspot</span></button>
          {isScreenshot && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setMode("blur"); red.addCentered("blur"); }} aria-label="Add redact" title="Add a redaction box (R)"><IconBlur /><span className="tb-label opt">Add redact</span></button>
          )}
          <button type="button" className="btn btn-sm btn-ghost" onClick={addCallout} disabled={!!co.callout} aria-label="Callout" title="Add a text callout to this screen (C)"><IconMessage /><span className="tb-label opt">Callout</span></button>
          <span className="tool-sep" aria-hidden />
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => void openQuickPreview()}
            aria-label="Quick preview"
            title="Quick preview this screen in full player mode (P)"
          >
            <IconPlay /><span className="tb-label opt">Quick preview</span>
          </button>
          {compact && <><span className="tool-sep" aria-hidden />{zoomGroup}</>}
          <span className="spacer" />
          <span className={`canvas-save-status ${manualSaving ? "saving" : saveStatus}`}>
            {manualSaving && "Saving…"}
            {!manualSaving && saveStatus === "error" && (error || "Save failed (retry)")}
          </span>
          {unsaved && <button type="button" className="btn btn-sm btn-ghost" onClick={discard}>Discard</button>}
          <button
            type="button"
            className={`btn btn-sm ${saveStatus === "error" ? "btn-primary text-danger" : unsaved || saveStatus === "dirty" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => void save(true)}
            disabled={manualSaving || (!!step.captureKey && html === null)}
            title={saveStatus === "error" ? "Click to retry save" : "Save changes (Ctrl+S)"}
          >
            {manualSaving ? "Saving…" : saveStatus === "error" ? "Retry save" : unsaved || saveStatus === "dirty" ? "Save" : "Saved"}
          </button>
          {compact && (
            <button type="button" className={`btn btn-sm ${inspectorOpen ? "btn-primary" : ""}`} onClick={onToggleInspector}
              aria-expanded={inspectorOpen} aria-controls={DRAWER_ID.inspector} title="Show or hide the inspector (I)">
              <IconSettings />Inspector
            </button>
          )}
        </div>
        {error && <div className="error-box studio-error">{error}</div>}

        <div ref={area} className={`editor-area ${zoom > 1 ? "zoomed" : ""}`}>
          <div className="stage-col">
          <div className="stage-bar" style={{ width: fitWidth || undefined }}>
            <span className="dots" aria-hidden><i /><i /><i /></span>
            <span className="stage-label">{step.textCard ? (step.textCard.role === "intro" ? "Intro screen" : step.textCard.role === "outro" ? "Outro screen" : "Text card") : step.captureKey ? "HTML capture" : step.mediaType === "video" ? "Video" : "Screenshot"} · {step.imageWidth}×{step.imageHeight}</span>
          </div>
          <div className="editor-stage" style={{ width: fitWidth || undefined }}>
            {html === null ? <p className="muted" style={{ padding: 24 }}>Loading page…</p> : (
              <>
                {step.captureKey ? (
                  <CaptureFrame ref={frame} srcDoc={html} width={step.imageWidth} height={step.imageHeight} sameOrigin onLoad={prepare} />
                ) : (
                  <MediaFrame step={step} index={index} total={total} redactions={mode === "blur" ? [] : red.redactions} />
                )}
                {mode === "hotspots" && (
                  <div
                    ref={c.canvasRef}
                    className="hs-layer"
                    {...c.handlers}
                    onPointerMove={(e) => { if (step.captureKey) updatePassthrough(e); c.handlers.onPointerMove(e); }}
                    onPointerLeave={(e) => { if (step.captureKey) leavePassthrough(e); }}
                  >
                    <HotspotBoxes c={c} />
                  </div>
                )}
                {step.captureKey && (mode === "blur" || mode === "hide") && (
                  <div className="pick-layer" onPointerMove={onPickMove} onPointerLeave={(e) => { setHover(null); leavePassthrough(e); }} onPointerDown={onPick}>
                    {hover && <div className={`pick-box ${mode}`} style={{ left: hover.x, top: hover.y, width: hover.width, height: hover.height }} />}
                  </div>
                )}
                {isScreenshot && mode === "blur" && (
                  <div
                    ref={red.canvasRef}
                    className="redaction-layer"
                    {...red.handlers}
                  >
                    <RedactionBoxes c={red} />
                  </div>
                )}
                {co.callout && !staysOnScreen(co.callout) && (
                  <div className="callout-page-layer callout-ref" ref={scaleCalloutLayer} style={calloutLayerStyle(step.imageWidth, step.imageHeight, calloutShift.dx, calloutShift.dy)}>
                    <CalloutCard co={co} pageShift={calloutShift} tip={calloutTipAt} stepLabel={stepLabel} onDrop={() => attachCallout(true)} />
                  </div>
                )}
                {step.focus && (
                  <FocusBoundaryBox
                    ref={focusBoxRef}
                    step={step}
                    onSaved={onSaved}
                    scrollShift={step.captureKey ? scrollShift : undefined}
                    onVisualChange={(b) => {
                      liveVisualFocus.current = b;
                    }}
                  />
                )}
              </>
            )}
            {co.callout && staysOnScreen(co.callout) && (
              <div className="stage-overlay-layer callout-ref" ref={scaleCalloutLayer} style={calloutLayerStyle(step.imageWidth, step.imageHeight)}>
                <CalloutCard co={co} stepLabel={stepLabel} onDrop={() => attachCallout(true)} />
              </div>
            )}
          </div>
          </div>
        </div>
        {!compact && zoomGroup}
        {!compact && <p className="studio-hint small"><IconInfo className="icon icon-xs" />{hint}</p>}
      </section>

      <aside id={DRAWER_ID.inspector} className={`editor-side studio-inspector ${inspectorOpen ? "open" : ""}`} aria-label="Inspector">
        <header className="studio-pane-head insp-head">
          <IconSettings /><h2>Inspector</h2><span className="count">Screen {String(index + 1).padStart(2, "0")}</span>
          {compact && <button type="button" className="drawer-x" onClick={onToggleInspector} aria-label="Close the inspector" title="Close (Esc)"><IconX /></button>}
        </header>
        <div className="insp-body">
          {/* Compact: no room for the hint under the canvas, so it leads the inspector instead. */}
          {compact && mode === "hotspots" && <p className="insp-hint small"><IconInfo className="icon icon-xs" />{hint}</p>}
          {isScreenshot && mode === "blur" ? (
            red.selected ? (
              <>
                <div className="insp-back"><button type="button" className="crumb" onClick={() => red.setSel(null)}>All redactions</button></div>
                <RedactionProps
                  r={red.selected}
                  n={red.redactions.findIndex((r) => r.id === red.sel) + 1}
                  onChange={red.patchSel}
                  onDelete={red.removeSel}
                />
              </>
            ) : (
              <div className="panel stack-s">
                <h3>Redactions <span className="count">{red.redactions.length}</span></h3>
                {red.redactions.length === 0 ? (
                  <p className="small muted">No redactions on this screenshot yet. Drag across any sensitive area to blur or blackout.</p>
                ) : (
                  <ul className="hs-rows">
                    {red.redactions.map((r, i) => (
                      <li key={r.id}>
                        <button type="button" className="hs-row" onClick={() => red.setSel(r.id)}>
                          <span className="n">{i + 1}</span>
                          <span>{r.type === "blackout" ? "Blackout" : "Blur"} ({r.w}% × {r.h}%)</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="row" style={{ gap: 6 }}>
                  <button type="button" className="btn btn-sm" onClick={() => red.addCentered("blur")}>Add blur box</button>
                  <button type="button" className="btn btn-sm" onClick={() => red.addCentered("blackout")}>Add blackout box</button>
                </div>
              </div>
            )
          ) : mode !== "hotspots" ? (
            <>
              <div className="panel stack-s">
                <h3>{MODES.find(([k]) => k === mode)![1]}</h3>
                <p className="small muted">{hint}</p>
                <p className="small muted">Undo and redo step through every change on this screen: hotspots, text, blur and hide.</p>
              </div>
              {mode === "hide" && (
                <div className="panel stack-s">
                  <label className="row">
                    <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
                    Show hidden ({hiddenEls.length})
                  </label>
                  {showHidden && hiddenEls.length > 0 && (
                    <div className="stack-s hidden-list">
                      {hiddenEls.map((el, i) => (
                        <div key={i} className="row hidden-item">
                          <span className="small muted mono">{describe(el)}</span>
                          <button type="button" className="btn btn-sm btn-ghost" onClick={() => restore(el)}>Restore</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : c.selected ? (
            <>
              <div className="insp-back"><button type="button" className="crumb" onClick={() => c.setSel(null)}>All hotspots</button></div>
              <HotspotProps h={c.selected} n={c.hs.findIndex((x) => x.id === c.sel) + 1} steps={demo.steps} currentStepId={step.id} onChange={c.patchSel} onDelete={c.removeSel} />
            </>
          ) : (
            <div className="panel stack-s">
              <h3>Hotspots <span className="count">{c.hs.length}</span></h3>
              {c.hs.length === 0 ? (
                <p className="small muted">No hotspots on this screen yet. Drag across the part of the {step.captureKey ? "page" : step.mediaType === "video" ? "video" : "screenshot"} the viewer should click.</p>
              ) : (
                <ul className="hs-rows">
                  {c.hs.map((h, i) => (
                    <li key={h.id}><button type="button" className="hs-row" onClick={() => c.setSel(h.id)}><span className="n">{i + 1}</span>{actionLabel(h)}</button></li>
                  ))}
                </ul>
              )}
              <div><button className="btn btn-sm" onClick={c.addCentered}>Add hotspot</button></div>
            </div>
          )}
          {co.callout && (
            <div className="panel stack-s">
              <h3>Callout</h3>
              <p className="small muted">Shows with this screen. Write it on the canvas and drag its bar to move it. Personalised values like {"{{company|your team}}"} work.</p>

              <label className="row" style={{ gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={!!co.callout.modal}
                  onChange={(e) => co.patch({ modal: e.target.checked })}
                />
                <span>Center-stage modal (dim background)</span>
              </label>

              <label className="row" style={{ gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={co.callout.showButton !== false && co.callout.showNextButton !== false}
                  onChange={(e) => co.patch({ showButton: e.target.checked, showNextButton: e.target.checked })}
                />
                <span>Show "Next" action button</span>
              </label>


              {!co.callout.modal && isAttached(co.callout) && (
                <div className="field">
                  <span>Position</span>
                  <span className="hint small muted" style={{ fontSize: "11px" }}>
                    Attached to what its arrow points at: it follows that as the page scrolls, at any window size. Drag the card to point it at something else; choose no arrow to place it freely.
                  </span>
                </div>
              )}
              {!co.callout.modal && !isAttached(co.callout) && (
                <div className="field">
                  <span>Position Mode</span>
                  <div className="tool-group" role="radiogroup" aria-label="Callout position mode" style={{ width: "100%", display: "flex" }}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!staysOnScreen(co.callout)}
                      className={`tool ${!staysOnScreen(co.callout) ? "on" : ""}`}
                      style={{ flex: 1, textAlign: "center", justifyContent: "center" }}
                      onClick={() => setCalloutMode("pinned")}
                      title="The callout sits at a spot on the page. Scroll down and it comes into view; scroll back up and it is still there"
                    >
                      Moves with Page
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={staysOnScreen(co.callout)}
                      className={`tool ${staysOnScreen(co.callout) ? "on" : ""}`}
                      style={{ flex: 1, textAlign: "center", justifyContent: "center" }}
                      onClick={() => setCalloutMode("sticky")}
                      title="The callout stays in the same place on the screen, however the viewer scrolls the page"
                    >
                      Stays on Screen
                    </button>
                  </div>
                  <span className="hint small muted" style={{ fontSize: "11px", marginTop: 2 }}>
                    {staysOnScreen(co.callout)
                      ? "Stays in the same place on the screen, even when the viewer scrolls the page."
                      : "Sits at this spot on the page. Scroll the page on the canvas to put it lower down; the viewer scrolls to reach it, and it stays there when they scroll back up."}
                  </span>
                </div>
              )}


              <div className="field">
                <span>Pointer arrow</span>
                <div className="tool-group" role="radiogroup" aria-label="Pointer arrow direction">
                  {(["none", "top", "bottom", "left", "right"] as const).map((dir) => (
                    <button
                      key={dir}
                      type="button"
                      role="radio"
                      aria-checked={(co.callout?.arrow ?? "none") === dir}
                      className={`tool ${(co.callout?.arrow ?? "none") === dir ? "on" : ""}`}
                      onClick={() => setCalloutArrow(dir)}
                    >
                      {dir === "none" ? "None" : dir[0].toUpperCase() + dir.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <span>Theme</span>
                <div className="tool-group" role="radiogroup" aria-label="Callout theme">
                  {(["default", "dark", "accent"] as const).map((th) => (
                    <button
                      key={th}
                      type="button"
                      role="radio"
                      aria-checked={(co.callout?.theme ?? "default") === th}
                      className={`tool ${(co.callout?.theme ?? "default") === th ? "on" : ""}`}
                      onClick={() => co.patch({ theme: th })}
                    >
                      {th[0].toUpperCase() + th.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid-2 small">
                <label className="field">
                  <span>Width (px)</span>
                  <input
                    type="number"
                    min={220}
                    max={800}
                    step={10}
                    placeholder="300"
                    value={co.callout.width ?? ""}
                    onChange={(e) => {
                      const val = e.target.value === "" ? null : Math.min(800, Math.max(220, Number(e.target.value) || 300));
                      co.patch({ width: val });
                    }}
                  />
                </label>
                <div />
              </div>

              {!co.callout.modal && !isAttached(co.callout) && (() => {
                const max = staysOnScreen(co.callout) ? 100 : MAX_CALLOUT_PCT;
                const clamp = (v: number) => Math.min(max, Math.max(0, v || 0));
                return (
                  <>
                    <div className="grid-2 small">
                      <label className="field"><span>{staysOnScreen(co.callout) ? "Screen Left %" : "Page Left %"}</span>
                        <input type="number" min={0} max={max} step={0.5} value={co.callout.x} onChange={(e) => co.patch({ x: clamp(Number(e.target.value)) })} />
                      </label>
                      <label className="field"><span>{staysOnScreen(co.callout) ? "Screen Top %" : "Page Top %"}</span>
                        <input type="number" min={0} max={max} step={0.5} value={co.callout.y} onChange={(e) => co.patch({ y: clamp(Number(e.target.value)) })} />
                      </label>
                    </div>
                    {!staysOnScreen(co.callout) && step.captureKey && (
                      <div><button type="button" className="btn btn-sm btn-ghost" onClick={scrollToCallout} title="Scroll the page on the canvas to where this callout sits">Scroll to callout</button></div>
                    )}
                  </>
                );
              })()}
              <div><button className="btn btn-danger btn-sm" onClick={co.remove}>Remove callout</button></div>
            </div>
          )}
          {stepPanel}
        </div>
        <div className="insp-foot">
          {stepActions}
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--line)" }}>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={async () => {
                if (await save()) go("survey");
              }}
            >
              Continue to Survey →
            </button>
          </div>
        </div>
      </aside>

      {quickPreview && (
        <div
          className="quick-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Quick Step Preview"
        >
          <div className="quick-preview-bar">
            <div className="quick-preview-title">
              <IconPlay className="icon icon-s" />
              <span>Quick Preview: Screen {index + 1} of {total}</span>
              <span className="quick-preview-badge">Live Step Preview</span>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => setQuickPreview(false)}
                title="Close preview (Escape)"
              >
                Close Preview <kbd style={{ marginLeft: 6, fontSize: "10px", opacity: 0.8 }}>Esc</kbd>
              </button>
            </div>
          </div>
          <div
            className="quick-preview-content"
            onClick={(e) => {
              if (e.target === e.currentTarget) setQuickPreview(false);
            }}
          >
            {quickStart ? (
              <Player bundle={demo} mode="preview" forcedOutcomeId={pathId} initialStepIndex={quickStart.index} />
            ) : (
              <Player bundle={demo} mode="preview" singleStepOnly initialStepIndex={demo.steps.findIndex((s) => s.id === step.id)} />
            )}
          </div>
        </div>
      )}
    </>
  );
});
export default StepCanvas;
